import { Box, Typography } from "@mui/material";
import { TFunction } from "i18next";

import { IEvidenceFile } from "@/types/typesAlerts";

import { entityIcon } from "./entityIcon";
import {
  BulletinEntityResource,
  EntityRow,
  toEntityRows,
  vehicleMakeModel,
} from "./entityRows";

/**
 * How a bulletin's photo is captioned with the entities it shows.
 *
 * Lives here rather than in `MediaGallery`, which takes a caption as a plain node: `libs/ui` owns no
 * domain vocabulary, and a gallery that knew what an entity was could only ever show bulletins.
 * Lives here rather than in either caller because both the detail view and the board card caption
 * the same photo the same way — the projection is shared, the two sizes are a parameter.
 *
 * Two layers. The white chips identify what the photo is **of**, and only ever that: a person's name
 * and date of birth, a vehicle's make and model, taken from the first entity the file is linked to.
 * The dark chips under them name every entity the photo is tagged with, in the words and numbers the
 * entity list uses, so "photo C shows Suspect #2" reads the same in both places, behind the type's
 * glyph because a person and a vehicle can both be "Suspect #1".
 */

/** Styles rather than components, so this file exports nothing fast refresh has to reload. */
const identitySx = (dense: boolean) => ({
  display: "block",
  width: "fit-content",
  bgcolor: "common.white",
  // Fixed dark text: the chip stays white over the photo in both modes, so
  // text.primary (white in dark mode) would vanish. grey.900 matches light
  // mode's text.primary on white.
  color: "grey.900",
  px: dense ? 0.75 : 1.5,
  py: dense ? 0.25 : 0.75,
  mt: dense ? 0.25 : 0.75,
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  maxWidth: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const tagSx = (dense: boolean) => ({
  display: "flex",
  alignItems: "center",
  gap: "3px",
  flex: "0 0 auto",
  px: dense ? "5px" : "7px",
  py: dense ? "2px" : "4px",
  borderRadius: dense ? "3px" : "4px",
  bgcolor: "rgba(0,0,0,0.68)",
  color: "common.white",
  fontSize: 11,
  fontWeight: 500,
  lineHeight: 1,
  whiteSpace: "nowrap",
});

/**
 * Who or what the photo is of.
 *
 * Short on purpose: this sits over a photo, where a line that wraps or truncates says less than no
 * line at all. Everything else the record holds — the plate, the vehicle type, the officer's own
 * description — is in the entity list under the bulletin.
 */
const identityLines = (
  entity: BulletinEntityResource,
  row: EntityRow,
): string[] => {
  switch (entity.entity_type) {
    case "person":
      return row.name ? [row.name, row.dateOfBirth].filter(Boolean) : [];
    case "vehicle":
      return [vehicleMakeModel(entity)].filter(Boolean);
    case "item":
      return [];
  }
};

/**
 * @param dense Sized for a board card's small hero, or a cluster of them, rather than a full one.
 */
export const mediaEntityCaption = (
  media: Pick<IEvidenceFile, "entity_ids">,
  entities: BulletinEntityResource[],
  t: TFunction,
  { dense = false }: { dense?: boolean } = {},
) => {
  const entityIds = media.entity_ids ?? [];
  if (entityIds.length === 0) return undefined;

  // Through the shared projection rather than off the entity, because the number in "Suspect #1" is
  // a property of the entity's place among its own type and not of the entity itself.
  const rows = toEntityRows(entities, t);
  const tagged = entityIds
    .map((entityId) => ({
      entity: entities.find((candidate) => candidate.id === entityId),
      row: rows.find((row) => row.id === entityId),
    }))
    .filter(
      (tag): tag is { entity: BulletinEntityResource; row: EntityRow } =>
        !!tag.entity && !!tag.row,
    );
  if (tagged.length === 0) return undefined;

  const identity = identityLines(tagged[0].entity, tagged[0].row);
  const variant = dense ? "caption" : "body2";

  return (
    <>
      {identity.map((line) => (
        <Typography key={line} variant={variant} sx={identitySx(dense)}>
          {line}
        </Typography>
      ))}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: dense ? "3px" : "4px",
          mt: identity.length > 0 ? (dense ? "3px" : "6px") : 0,
          maxWidth: "100%",
        }}
      >
        {tagged.map(({ row }) => (
          <Box key={row.id} sx={tagSx(dense)}>
            {entityIcon(row.type, 12)}
            {row.heading}
          </Box>
        ))}
      </Box>
    </>
  );
};
